import { CSSObject, styled, Theme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import {
  getSidenavBackgroundColor,
  getSidenavBorderColor,
  getSidenavTextColor,
  isSidenavLightSurface,
} from './sidenav-colors';
import { SIDENAV_TOKENS } from './sidenav-tokens';

const DRAWER_WIDTH = SIDENAV_TOKENS.drawerWidth;
const DRAWER_WIDTH_COLLAPSED = SIDENAV_TOKENS.collapsedWidth;

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH_COLLAPSED,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
});

/** Scrollbar chrome for the dark rail (overrides global light-surface scrollbar rules). */
function getDarkSurfaceScrollbarSx(): CSSObject {
  return {
    scrollbarColor: 'rgba(255, 255, 255, 0.35) rgba(255, 255, 255, 0.08)',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: 6,
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.08)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.35)',
      borderRadius: 6,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
  };
}

/** Scrollbar chrome for the light rail (subtle dark thumbs on soft gray). */
function getLightSurfaceScrollbarSx(): CSSObject {
  return {
    scrollbarColor: 'rgba(0, 0, 0, 0.25) rgba(0, 0, 0, 0.04)',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: 6,
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(0, 0, 0, 0.04)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.25)',
      borderRadius: 6,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(0, 0, 0, 0.4)',
    },
  };
}

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => {
  const lightSurface = isSidenavLightSurface(theme);
  const borderColor = getSidenavBorderColor(theme);

  return {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open ? openedMixin(theme) : closedMixin(theme)),
    '& .MuiDrawer-paper': {
      backgroundColor: getSidenavBackgroundColor(theme),
      color: getSidenavTextColor(theme),
      overflowY: 'auto',
      // Light: gray-200 edge + soft shadow so the rail reads as a control panel.
      borderRight: lightSurface ? `1px solid ${borderColor}` : 'none',
      boxShadow: lightSurface ? '2px 0 8px rgba(0, 0, 0, 0.04)' : 'none',
      ...(lightSurface ? getLightSurfaceScrollbarSx() : getDarkSurfaceScrollbarSx()),
      ...(open ? openedMixin(theme) : closedMixin(theme)),
      '& .visible-collapsed': {
        display: open ? 'none' : 'inline-block',
      },
      '& .hidden-collapsed': {
        display: open ? 'block' : 'none',
      },
    },
  };
});
